import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Divider from 'src/components/ui/Divider/Divider';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { showSpinnerToast, hideSpinnerToast } from 'src/utils/spinnerToaster';
import { InboundCreateShell } from './InboundCreateData';
import { ActionBar, InboundConfiguratorForm, Section } from './InboundConfiguratorForm';
import {
  formValuesToInboundOrder,
  inboundFormDataStepSchema,
  inboundFormSummaryStepSchema,
  InboundFormSummaryStepValues,
  useInboundFormContext,
} from './InboundFormContext';
import { useInboundOrderMutations } from '../../services/inbound';

const InboundCreateSummary: React.FC = () => {
  const { t } = useTranslation();

  const { formState, setFormState } = useInboundFormContext();

  const { handleSubmit } = useForm<InboundFormSummaryStepValues>({
    resolver: zodResolver(inboundFormSummaryStepSchema),
    defaultValues: formState.summary,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createInboundOrderAsync } = useInboundOrderMutations();
  const navigate = useNavigate();

  useEffect(() => {
    if (formState) {
      const previousStepData = formState.data;
      const { success } = inboundFormDataStepSchema.safeParse(previousStepData);
      if (!success) {
        navigate('/warehouse/inbound/create/data');
      }
    }
  }, [formState]);

  const onSubmit = async (data: InboundFormSummaryStepValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    showSpinnerToast(t('inbound.creating_order'));

    try {
      const newData = structuredClone(formState);
      newData.summary = data;
      setFormState(() => newData);

      const createdOrder = await createInboundOrderAsync({
        body: formValuesToInboundOrder(newData),
      });

      if (createdOrder) {
        document.querySelector('.main-content-wrapper')?.scrollTo({ top: 0, behavior: 'smooth' });

        // Inbound-Draft spec 2026-11-03 §6: the wizard creates a DRAFT; the detail page
        // offers the confirm (with the terms) and shows the delivery slip afterwards.
        navigate(`/warehouse/inbound/${createdOrder.id}?created=1`);
      }
    } catch (error: any) {
      console.error('Create inbound order failed:', error);

      const message = error?.response?.data?.message || error?.message || error?.detail || t('errors.unknown');

      toast.error(message);
    } finally {
      hideSpinnerToast();
      setIsSubmitting(false);
    }
  };

  return (
    <InboundCreateShell
      step={2}
      headline={t('inbound.create_headline')}
      title={`${t('inbound.inbound')} / ${t('inbound.create_cta')}`}>
      <Section className="flex flex-col gap-y-32">
        <InboundConfiguratorForm readOnly />
        <Divider className="" />
        <div className="flex justify-between gap-12">
          <Button
            title={t('inbound.back')}
            tone={ButtonToneProps.GHOST}
            icon="arrowLeft"
            onClick={() => navigate('/warehouse/inbound/create/data')}
          />
          <ActionBar>
            <Button
              title={t('inbound.cancel')}
              tone={ButtonToneProps.SECONDARY}
              onClick={() => {
                navigate('/warehouse/inbound');
              }}
            />
            <Button
              title={t('inbound.create_draft_cta')}
              onClick={() => {
                handleSubmit(onSubmit)();
              }}
              tone={ButtonToneProps.PRIMARY_BLUE}
              disabled={isSubmitting}
            />
          </ActionBar>
        </div>
      </Section>
    </InboundCreateShell>
  );
};

export default InboundCreateSummary;
